import React from "react";
import { Box, MenuItem, TextField, Typography, styled } from "@mui/material";
import { useTbsTenant } from "../../hooks/tbs/useTbsTenant";

// Which company you are working in — APP-1 (`TBS-47`).
//
// **It renders nothing at all with one membership**, which is the case for everyone today. A control
// offering a single option is not a choice; it is a permanent reminder of a decision nobody has to
// make, on a screen a cashier uses all day.
//
// When there are several it is deliberately prominent rather than tucked into a menu. Every figure
// on every management screen belongs to one company, and the cost of not noticing which is somebody
// entering a day's sales into the wrong one.

const Bar = styled(Box, { shouldForwardProp: (prop) => prop !== "dense" })<{ dense?: boolean }>(
  ({ theme, dense }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: dense ? 0 : theme.spacing(2),
  }),
);

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

interface Props {
  /**
   * Drops the bottom margin. Set by `TbsHeader`, where this sits in a row of controls rather than
   * above the page's content — which is where it lived until `NAV-1` gave the app a header.
   */
  dense?: boolean;
}

export const TenantSelector: React.FC<Props> = ({ dense = false }) => {
  const { tenantId, memberships, offerChoice, selectTenant } = useTbsTenant();

  if (!offerChoice) return null;

  return (
    <Bar dense={dense}>
      <Label variant="body2">Empresa</Label>
      <TextField
        select
        size="small"
        // **Never uncontrolled.** With several memberships and nothing stored, `tenantId` is
        // deliberately empty — the answer is "ask", not "assume the first" — so the field needs an
        // explicit placeholder rather than silently showing the first option as if it were chosen.
        value={tenantId}
        onChange={(event) => selectTenant(event.target.value)}
        inputProps={{ "aria-label": "Empresa" }}
        sx={{ minWidth: 220 }}
      >
        {!tenantId && (
          <MenuItem value="" disabled>
            Seleccione una empresa
          </MenuItem>
        )}
        {memberships.map((membership) => (
          <MenuItem key={membership.id} value={membership.id}>
            {membership.slug}
          </MenuItem>
        ))}
      </TextField>
    </Bar>
  );
};

export default TenantSelector;
